import { Check, Upload } from "lucide-react";
import { type FC, useRef, useState } from "react";

import { cn } from "@/lib/utils";
import { m } from "@recobra/i18n/messages";
import { Badge } from "@recobra/ui/badge";
import { Button } from "@recobra/ui/button";

export const DocumentDropzone: FC<{
  onFiles: (files: File[]) => void;
  disabled?: boolean;
  multiple?: boolean;
  fileCount?: number;
  allDone?: boolean;
  disabledHint?: string;
  maxFiles?: number;
}> = ({
  onFiles,
  disabled = false,
  multiple = true,
  fileCount,
  allDone = false,
  disabledHint,
  maxFiles,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  const pickFiles = (list: FileList | null) => {
    if (!list || disabled) return;
    const files = Array.from(list);
    if (files.length > 0) onFiles(files);
  };

  const hasFiles = (fileCount ?? 0) > 0;

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        className="sr-only"
        multiple={multiple}
        disabled={disabled}
        onChange={(e) => pickFiles(e.target.files)}
      />
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled}
        onKeyDown={(e) => !disabled && e.key === "Enter" && inputRef.current?.click()}
        onClick={() => !disabled && inputRef.current?.click()}
        onDragEnter={(e) => {
          if (disabled) return;
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          if (!disabled) pickFiles(e.dataTransfer.files);
        }}
        className={cn(
          "relative flex min-h-28 flex-col items-center justify-center overflow-hidden rounded-xl border border-dashed border-input p-4 transition-colors select-none",
          dropzoneStateClassName({ disabled, dragging }),
        )}
      >
        <div className="flex flex-col items-center justify-center gap-2 text-center pointer-events-none">
          <div
            className="bg-background flex size-10 shrink-0 items-center justify-center rounded-full border"
            aria-hidden="true"
          >
            <Upload className="size-4 opacity-60" aria-hidden="true" />
          </div>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <p className="text-sm font-medium text-foreground">{m.case_dropzone_instruction()}</p>
            <Button type="button" variant="outline" disabled={disabled} tabIndex={-1}>
              <Upload data-icon="inline-start" className="opacity-60" aria-hidden="true" />
              {m.case_dropzone_select_button()}
            </Button>
            {hasFiles && (
              <Badge variant="secondary">
                {allDone && <Check className="size-3 mr-1 text-success" />}
                {m.case_dropzone_files_count({ count: fileCount! })}
              </Badge>
            )}
          </div>
        </div>
        {!hasFiles && (
          <p className="mt-2 text-xs text-muted-foreground">
            {disabled && disabledHint ? disabledHint : acceptHint(maxFiles)}
          </p>
        )}
      </div>
    </>
  );
};

const acceptHint = (maxFiles: number | undefined): string =>
  maxFiles === undefined
    ? m.case_dropzone_accept()
    : m.case_dropzone_accept_with_limit({ maxFiles });

const dropzoneStateClassName = ({
  disabled,
  dragging,
}: {
  disabled: boolean;
  dragging: boolean;
}): string => {
  if (disabled) return "cursor-not-allowed opacity-50 bg-muted/10";
  if (dragging) return "cursor-pointer border-ring bg-accent/50";

  return "cursor-pointer hover:bg-accent/10";
};
